import React from 'react'
import { Link } from '@tanstack/react-router'
import { Home, LogOut, Menu, User, X } from 'lucide-react'
import { Button } from '../primitives/button'
import { LanguageSwitcher } from '../navigation/LanguageSwitcher'
import type { UserRole } from '@shared/schemas'
import { cn, useAuthValue, useFirebaseAuth, useTranslation } from '@/core'
import { useUserDataValue } from '@/features/users'

interface DashboardHeaderProps {
  className?: string
}

export function DashboardHeader({ className }: DashboardHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [adminMenuOpen, setAdminMenuOpen] = React.useState(false)
  const [bookingsMenuOpen, setBookingsMenuOpen] = React.useState(false)
  const { logout } = useFirebaseAuth()
  const { t } = useTranslation()
  const auth = useAuthValue()
  const userData = useUserDataValue()
  // Only admin can access administration section (not employee)
  const isAdmin = userData?.role === 'admin'

  const mainNavLinks = [
    {
      label: t('nav.dashboard'),
      to: '/',
      role: ['admin', 'corporate', 'doctor', 'employee'] as Array<UserRole>,
    },
    {
      label: t('nav.documents'),
      to: '/documents',
      role: ['admin', 'corporate', 'doctor', 'employee'] as Array<UserRole>,
    },
    {
      label: t('nav.support'),
      to: '/tickets',
      role: ['admin', 'corporate', 'doctor', 'employee'] as Array<UserRole>,
    },
    {
      label: t('nav.corporate'),
      to: '/corporate',
      role: ['corporate'] as Array<UserRole>,
    },
  ]

  const bookingNavLinks = [
    {
      label: t('nav.bookings'),
      to: userData?.role === 'corporate' ? '/corporate/bookings' : '/bookings',
      role: ['admin', 'doctor', 'employee', 'corporate'] as Array<UserRole>,
      preload: 'render',
    },
    {
      label: t('nav.weeklyPlanning'),
      to: '/weekly-plan',
      role: ['admin'] as Array<UserRole>,
    },
    {
      label: t('nav.bookingSearch'),
      to: '/bookings/search',
      role: ['admin'] as Array<UserRole>,
    },
  ].filter((link) => link.role.includes(userData?.role ?? 'employee'))

  const adminNavLinks = [
    {
      label: t('nav.users'),
      to: '/users',
      role: ['admin'] as Array<UserRole>,
    },
    {
      label: t('nav.companies'),
      to: '/companies',
      role: ['admin'] as Array<UserRole>,
    },
    {
      label: t('nav.patients'),
      to: '/patients',
      role: ['admin'] as Array<UserRole>,
    },
    {
      label: t('nav.jobRoles'),
      to: '/job-roles',
      role: ['admin'] as Array<UserRole>,
    },
    {
      label: t('nav.exams'),
      to: '/exams',
      role: ['admin'] as Array<UserRole>,
    },
    {
      label: t('nav.siteInspections'),
      to: '/site-inspections',
      role: ['admin'] as Array<UserRole>,
    },
    {
      label: t('nav.clinics'),
      to: '/clinics',
      role: ['admin'] as Array<UserRole>,
    },
    {
      label: t('nav.sendEmail'),
      to: '/admin-email',
      role: ['admin'] as Array<UserRole>,
    },
  ]

  const handleLogout = () => {
    logout()
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-50 border-b border-gray-200 bg-white shadow-sm',
        className,
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Brand */}
          <Link
            to="/"
            className="flex items-center gap-2 font-bold text-xl text-gray-900 hover:text-blue-600 transition-colors"
          >
            <Home className="size-6" />
            <span className="hidden sm:inline">IRV MDL</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {mainNavLinks
              .filter((link) =>
                link.role.includes(userData?.role ?? 'employee'),
              )
              .map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  activeOptions={{ exact: true }}
                >
                  {({ isActive }: { isActive: boolean }) => (
                    <span
                      className={cn(
                        'px-3 py-2 rounded-md text-sm font-medium transition-all duration-200',
                        isActive
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100',
                      )}
                    >
                      {link.label}
                    </span>
                  )}
                </Link>
              ))}

            {/* Bookings Dropdown Menu */}
            {bookingNavLinks.length > 1 ? (
              <div className="relative">
                <button
                  onClick={() => {
                    if (!bookingsMenuOpen) setAdminMenuOpen(false)
                    setBookingsMenuOpen(!bookingsMenuOpen)
                  }}
                  className={cn(
                    'px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-1',
                    bookingsMenuOpen
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100',
                  )}
                >
                  {t('nav.bookings')}
                  <svg
                    className={cn(
                      'w-4 h-4 transition-transform duration-200',
                      bookingsMenuOpen && 'rotate-180',
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {bookingsMenuOpen && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                    {bookingNavLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        activeOptions={{ exact: true }}
                        onClick={() => setBookingsMenuOpen(false)}
                      >
                        {({ isActive }: { isActive: boolean }) => (
                          <span
                            className={cn(
                              'block px-4 py-2 text-sm transition-colors',
                              isActive
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-50',
                            )}
                          >
                            {link.label}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              bookingNavLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  activeOptions={{ exact: true }}
                >
                  {({ isActive }: { isActive: boolean }) => (
                    <span
                      className={cn(
                        'px-3 py-2 rounded-md text-sm font-medium transition-all duration-200',
                        isActive
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100',
                      )}
                    >
                      {link.label}
                    </span>
                  )}
                </Link>
              ))
            )}

            {/* Admin Dropdown Menu */}
            {isAdmin && (
              <div className="relative">
                <button
                  onClick={() => {
                    if (!adminMenuOpen) setBookingsMenuOpen(false)
                    setAdminMenuOpen(!adminMenuOpen)
                  }}
                  className={cn(
                    'px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-1',
                    adminMenuOpen
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100',
                  )}
                >
                  {t('nav.administration')}
                  <svg
                    className={cn(
                      'w-4 h-4 transition-transform duration-200',
                      adminMenuOpen && 'rotate-180',
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {adminMenuOpen && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                    {adminNavLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        activeOptions={{ exact: true }}
                        onClick={() => setAdminMenuOpen(false)}
                      >
                        {({ isActive }: { isActive: boolean }) => (
                          <span
                            className={cn(
                              'block px-4 py-2 text-sm transition-colors',
                              isActive
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-50',
                            )}
                          >
                            {link.label}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* User Info */}
            {userData && (
              <Link
                to="/profile"
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
              >
                <div className="flex flex-col items-end text-xs">
                  <span className="font-semibold text-gray-900">
                    {userData.displayName ||
                      (userData.email ? userData.email.split('@')[0] : 'User')}
                  </span>
                  <span className="text-gray-500">{userData.email}</span>
                </div>
                <User className="size-4 text-gray-400" />
              </Link>
            )}

            {/* Logout Button */}
            {(userData || auth) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-700 hover:text-red-600 hover:bg-red-50"
                title={t('auth.logout')}
              >
                <LogOut className="size-4" />
                <span className="hidden sm:inline ml-2">
                  {t('auth.logout')}
                </span>
              </Button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="size-5" />
              ) : (
                <Menu className="size-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-gray-200 py-4">
            {/* Main Navigation Links */}
            {mainNavLinks
              .filter((link) =>
                link.role.includes(userData?.role ?? 'employee'),
              )
              .map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  activeOptions={{ exact: true }}
                >
                  {({ isActive }: { isActive: boolean }) => (
                    <span
                      className={cn(
                        'block px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 mb-1',
                        isActive
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100',
                      )}
                    >
                      {link.label}
                    </span>
                  )}
                </Link>
              ))}

            {/* Bookings Section */}
            <div className="px-4 py-2 mt-2 mb-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {t('nav.bookings')}
              </p>
            </div>
            {bookingNavLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                activeOptions={{ exact: true }}
              >
                {({ isActive }: { isActive: boolean }) => (
                  <span
                    className={cn(
                      'block px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 mb-1 ml-2',
                      isActive
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100',
                    )}
                  >
                    {link.label}
                  </span>
                )}
              </Link>
            ))}

            {/* Admin Section */}
            {isAdmin && (
              <>
                <div className="px-4 py-2 mt-2 mb-1">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('nav.administration')}
                  </p>
                </div>
                {adminNavLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileMenuOpen(false)}
                    activeOptions={{ exact: true }}
                  >
                    {({ isActive }: { isActive: boolean }) => (
                      <span
                        className={cn(
                          'block px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 mb-1 ml-2',
                          isActive
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-100',
                        )}
                      >
                        {link.label}
                      </span>
                    )}
                  </Link>
                ))}
              </>
            )}
          </nav>
        )}
      </div>
    </header>
  )
}
